/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   index.tsx
 *     Description     :   Minimal design-system theme module — class-based dark-mode toggle.
 *                         Flips the `.dark` class on <html> so the index.css tokens switch
 *                         between light and dark. Ported from the Fabric Control portal
 *                         theme mechanism (UserPreferencesContext / PreferencesPanel NOT
 *                         ported — only the core toggle + useTheme hook).
 *     Module          :   design-system-foundation (Constitution Article XVII)
 *     Component Type  :   Provider / Hook
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-06-24      Peter Dobler        Initial creation (design-system port)
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   react
 *     Internal        :   (none)
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     ThemeProvider, useTheme, type ThemeMode, type ResolvedTheme
 *
 * =========================================================================================
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "ngm-theme-mode";

export interface ThemeContextValue {
  /** The user's selected mode ('system' resolves to the OS preference). */
  mode: ThemeMode;
  /** The concrete theme currently applied to the document. */
  resolvedTheme: ResolvedTheme;
  /** Set the mode explicitly. */
  setMode: (mode: ThemeMode) => void;
  /** Convenience toggle between light and dark (sets an explicit mode). */
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readStoredMode(defaultMode: ThemeMode): ThemeMode {
  if (typeof window === "undefined") return defaultMode;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") return stored;
  return defaultMode;
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export interface ThemeProviderProps {
  children: ReactNode;
  defaultMode?: ThemeMode;
}

/**
 * Provides the design-system theme and applies the `.dark` class to
 * document.documentElement so the index.css tokens switch light/dark.
 */
export function ThemeProvider({ children, defaultMode = "system" }: ThemeProviderProps): React.JSX.Element {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode(defaultMode));
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  // Track the OS preference while in 'system' mode.
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const resolvedTheme: ResolvedTheme = mode === "system" ? (systemDark ? "dark" : "light") : mode;

  // Apply the resolved theme to the document root (the `.dark` token switch).
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.classList.toggle("dark", resolvedTheme === "dark");
    root.style.colorScheme = resolvedTheme;
  }, [resolvedTheme]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const toggleTheme = useCallback(() => {
    setMode(resolvedTheme === "dark" ? "light" : "dark");
  }, [resolvedTheme, setMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolvedTheme, setMode, toggleTheme }),
    [mode, resolvedTheme, setMode, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Access the design-system theme. Must be used within a {@link ThemeProvider}. */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
}
